import { useCallback, useEffect, useRef, useState } from 'react';

// Toca um chime (dois tons subindo) quando novos IDs aparecem no array
// de going. Persiste on/off em localStorage. Autoplay é bloqueado antes
// de um user gesture — o toggle serve como gesture inicial. Se o usuario
// nunca clicar, ficamos silenciosos sem quebrar nada.
//
// Uso:
//   const { enabled, toggle, ready } = useNewConfirmationSound(going, 'conv-42');
//
// - going: array [{ id }] — quando um id novo aparece, dispara chime
// - key: string opcional pra escopar preferencia por convocacao/rota

const LS_KEY = 'futezap:new-conf-sound:enabled';

export default function useNewConfirmationSound(items, key = 'default') {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === '1'; } catch { return false; }
  });
  const seenRef = useRef(new Set());
  const ctxRef = useRef(null);
  const firstRunRef = useRef(true);

  const ensureCtx = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      ctxRef.current = new Ctx();
    } catch { return null; }
    return ctxRef.current;
  }, []);

  const playChime = useCallback(() => {
    const ctx = ensureCtx();
    if (!ctx) return;
    // Dois tons curtos subindo — apito de gol curto.
    const now = ctx.currentTime;
    const notes = [
      { freq: 880, start: 0,    dur: 0.12 },
      { freq: 1320, start: 0.10, dur: 0.16 },
    ];
    for (const n of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = n.freq;
      gain.gain.setValueAtTime(0.0001, now + n.start);
      gain.gain.exponentialRampToValueAtTime(0.28, now + n.start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + n.start + n.dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + n.start);
      osc.stop(now + n.start + n.dur + 0.02);
    }
  }, [ensureCtx]);

  const toggle = useCallback(() => {
    setEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem(LS_KEY, next ? '1' : '0'); } catch {}
      if (next) {
        // User gesture — ativa AudioContext e toca um chime curto de teste.
        const ctx = ensureCtx();
        if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
        setTimeout(playChime, 50);
      }
      return next;
    });
  }, [ensureCtx, playChime]);

  useEffect(() => {
    const currentIds = new Set((items || []).map(x => String(x.id)).filter(Boolean));
    if (firstRunRef.current) {
      // Primeiro load: registra IDs vistos sem tocar (evita chime falso).
      seenRef.current = currentIds;
      firstRunRef.current = false;
      return;
    }
    const hasNew = [...currentIds].some(id => !seenRef.current.has(id));
    seenRef.current = currentIds;
    if (hasNew && enabled) playChime();
  }, [items, enabled, playChime, key]);

  return { enabled, toggle };
}
